import api from "../api/api";
import IAttachment, { IAttachmentResponseModel, mapAttachmentResponseModel } from "../interfaces/IAttachment";
import { GeoCoordinate } from "../interfaces/GeoCoordinate";
import IGeoCoordinate from "../interfaces/IGeoCoordinate";
import ILeaseComp, { ILeaseCompRequestModel, ILeaseCompResponseModel, ILeaseCompState } from "../interfaces/ILeaseComp";
import { parseDateTime } from "../utils/utils";

export default class LeaseCompsApiService {
    public async getLeaseComps(): Promise<ILeaseComp[]> {
        const response = await api.get<ILeaseCompResponseModel[]>("/leasecomp/get");
        return this.mapResponseModelsToLeaseComps(response.data);
    }

    public async createLeaseComp(leaseCompState: ILeaseCompState, pipedriveCompanyId: number | null, centerPoint: IGeoCoordinate, regridId: string | null): Promise<{ id: number, modifiedDate: Date, modifiedByUser: string }> {
        const response = await api.post("/leasecomp/create", this.mapLeaseCompStateToRequestModel(leaseCompState, pipedriveCompanyId, centerPoint, regridId));

        return {
            id: response.data.id,
            modifiedDate: parseDateTime(response.data.modifiedDate),
            modifiedByUser: response.data.modifiedByUser,
        };
    }

    public async updateLeaseComp(leaseComp: ILeaseComp): Promise<{ modifiedDate: Date, modifiedByUser: string }> {
        const response = await api.post("/leasecomp/update", this.mapLeaseCompToRequestModel(leaseComp));

        return {
            modifiedDate: parseDateTime(response.data.modifiedDate),
            modifiedByUser: response.data.modifiedByUser,
        };
    }

    public async deleteLeaseComp(id: number): Promise<void> {
        await api.delete(`/leasecomp/delete?id=${id}`);
    }

    public async uploadLeaseCompAttachment(leaseCompId: number, file: File): Promise<IAttachment> {
        const formData = new FormData();
        formData.append("file", file);

        const response = await api.post<IAttachmentResponseModel>(`/leasecomp/uploadAttachment?id=${leaseCompId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        return mapAttachmentResponseModel(response.data);
    }

    public async deleteLeaseCompAttachment(leaseCompId: number, key: string): Promise<void> {
        await api.delete(`/leasecomp/deleteAttachment?id=${leaseCompId}&key=${encodeURIComponent(key)}`);
    }

    private mapResponseModelsToLeaseComps(data: ILeaseCompResponseModel[]): ILeaseComp[] {
        return data.map(lc => {
            return {
                id: lc.id,
                address: lc.address,
                regridId: lc.regridId,
                signedDate: lc.signedDate ? parseDateTime(lc.signedDate) : null,
                landPsf: lc.landPsf,
                buildingPsf: lc.buildingPsf,
                totalRent: lc.totalRent,
                zoning: lc.zoning,
                propertyType: lc.propertyType,
                parcelSizeAcres: lc.parcelSizeAcres,
                tenantLandSize: lc.tenantLandSize,
                buildingSize: lc.buildingSize,
                options: lc.options,
                pipedriveCompanyId: lc.pipedriveCompanyId,
                notes: lc.notes,
                centerPoint: new GeoCoordinate(lc.latitude, lc.longitude),
                leaseExpiration: lc.leaseExpiration ? parseDateTime(lc.leaseExpiration) : null,
                type: lc.type,
                year: lc.year,
                freeRent: lc.freeRent,
                ti: lc.ti,
                escalations: lc.escalations,
                modifiedByUser: lc.modifiedByUser,
                modifiedDate: parseDateTime(lc.modifiedDate),
                compSource: lc.compSource,
                attachments: (lc.attachments ?? []).map(mapAttachmentResponseModel),
            };
        });
    }

    private mapLeaseCompStateToRequestModel(leaseCompState: ILeaseCompState, pipedriveCompanyId: number | null, centerPoint: IGeoCoordinate, regridId: string | null): ILeaseCompRequestModel {
        return {
            address: leaseCompState.address!,
            regridId: regridId,
            signedDate: leaseCompState.signedDate ? leaseCompState.signedDate.toISOString() : null,
            landPsf: leaseCompState.landPsf,
            buildingPsf: leaseCompState.buildingPsf,
            totalRent: leaseCompState.totalRent!,
            zoning: leaseCompState.zoning,
            propertyType: leaseCompState.propertyType,
            parcelSizeAcres: leaseCompState.parcelSizeAcres,
            tenantLandSize: leaseCompState.tenantLandSize,
            buildingSize: leaseCompState.buildingSize,
            options: leaseCompState.options,
            pipedriveCompanyId: pipedriveCompanyId,
            notes: leaseCompState.notes,
            latitude: centerPoint.latitude,
            longitude: centerPoint.longitude,
            leaseExpiration: leaseCompState.leaseExpiration ? leaseCompState.leaseExpiration.toISOString() : null,
            type: leaseCompState.type,
            year: leaseCompState.year,
            freeRent: leaseCompState.freeRent,
            ti: leaseCompState.ti,
            escalations: leaseCompState.escalations,
            compSource: leaseCompState.compSource,
        };
    }

    private mapLeaseCompToRequestModel(leaseComp: ILeaseComp): ILeaseCompRequestModel {
        return {
            id: leaseComp.id,
            address: leaseComp.address,
            regridId: leaseComp.regridId,
            signedDate: leaseComp.signedDate ? leaseComp.signedDate.toISOString() : null,
            landPsf: leaseComp.landPsf,
            buildingPsf: leaseComp.buildingPsf,
            totalRent: leaseComp.totalRent,
            zoning: leaseComp.zoning,
            propertyType: leaseComp.propertyType,
            parcelSizeAcres: leaseComp.parcelSizeAcres,
            tenantLandSize: leaseComp.tenantLandSize,
            buildingSize: leaseComp.buildingSize,
            options: leaseComp.options,
            pipedriveCompanyId: leaseComp.pipedriveCompanyId,
            notes: leaseComp.notes,
            latitude: leaseComp.centerPoint.latitude,
            longitude: leaseComp.centerPoint.longitude,
            leaseExpiration: leaseComp.leaseExpiration ? leaseComp.leaseExpiration.toISOString() : null,
            type: leaseComp.type,
            year: leaseComp.year,
            freeRent: leaseComp.freeRent,
            ti: leaseComp.ti,
            escalations: leaseComp.escalations,
            compSource: leaseComp.compSource,
        };
    }
}
